import React, { useState, useEffect } from 'react';
import { Typography, Box, TextField, Button, Paper, Container, Card, CardContent } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/apiClient';
import { useBusinessTheme } from '../contexts/BusinessThemeContext';

const BusinessSetup = () => {
  const [businessName, setBusinessName] = useState('');
  const [description, setDescription] = useState('');
  const [businessCode, setBusinessCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { refreshUser, user } = useAuth();
  const navigate = useNavigate();
  const { theme } = useBusinessTheme();
  
  // Get user's timezone
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await apiClient.post('/business', {
        name: businessName,
        description: description,
        businessCode: businessCode,
        timeZoneId: userTimeZone
      });
      
      // Force a complete auth refresh 
      console.log("Business created successfully, refreshing user data");
      await refreshUser();
      
      // Add a small delay to ensure state updates propagate
      setTimeout(() => {
        navigate('/dashboard');
      }, 100);
      
    } catch (err: any) {
      console.error("Business creation error:", err);
      setError(err.response?.data?.message || 'Failed to set up business. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Set Up Business | MindShare Calendar</title>
      </Helmet>
      <Container maxWidth="md" sx={{ mt: { xs: 2, sm: 4 }, mb: { xs: 2, sm: 4 } }}>
        <Card sx={{ backgroundColor: theme.accentColor }}>
          <CardContent>
            <Typography variant="h4" component="h1" gutterBottom align="center">
              Set Up Your Business
            </Typography>
            <Typography variant="subtitle1" align="center" color="textSecondary" paragraph>
              To continue, please provide some basic information about your business
            </Typography>

            {error && (
              <Typography color="error" align="center" paragraph>
                {error}
              </Typography>
            )}
            
            <Box component="form" onSubmit={handleSubmit} noValidate sx={{
                '& .MuiTextField-root': {
                    '& .MuiOutlinedInput-root': {
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#1976d2', // Force blue focus border
                            borderWidth: '2px'
                        }
                    }
                }
            }}>
              <TextField
                margin="normal"
                required
                fullWidth
                label="Business Name"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
              />
              
              <TextField
                margin="normal"
                fullWidth
                label="Business Code (Optional)"
                value={businessCode}
                onChange={(e) => setBusinessCode(e.target.value.toUpperCase())}
                placeholder="e.g., ABC"
                helperText="3-10 characters. Used for job numbering (e.g., ABC-20241201-001). Leave blank to auto-generate from business name."
                inputProps={{ maxLength: 10 }}
                error={businessCode.length > 0 && businessCode.length < 3}
                FormHelperTextProps={{
                  error: businessCode.length > 0 && businessCode.length < 3
                }}
              />
              
              <TextField
                margin="normal"
                fullWidth
                label="Business Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={4}
              />
              
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                sx={{ mt: 3 }}
                disabled={isLoading || !businessName.trim() || (businessCode.length > 0 && businessCode.length < 3)}
              >
                {isLoading ? 'Setting Up...' : 'Continue'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </>
  );
};

export default BusinessSetup; 